import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Copy, Heart, Trash2, Eye, Sparkles, TrendingDown, Send, MessageCircle } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { SecureAccountCard } from "../components/SecureAccountCard";

const LEVELS = { milestones: "Milestones only", full: "Milestones + daily logs" };
const MOOD_EMOJI = ["😔", "😟", "😐", "🙂", "😌"];

export default function Buddy() {
  const [data, setData] = useState({ supporters: [], supporting: [] });
  const [checkins, setCheckins] = useState([]);
  const [ci, setCi] = useState({ mood: 3, note: "" });
  const [shareLevel, setShareLevel] = useState("milestones");
  const [invite, setInvite] = useState(null);
  const [code, setCode] = useState("");
  const [view, setView] = useState(null);

  const load = () => {
    api.get("/buddy/list").then(({ data }) => setData(data)).catch(() => {});
    api.get("/buddy/checkins").then(({ data }) => setCheckins(data.checkins)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const sendCheckin = async () => {
    try { await api.post("/buddy/checkin", ci); toast.success("Check-in shared with your support team. 💚"); setCi({ mood: 3, note: "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const genInvite = async () => {
    try { const { data } = await api.post("/buddy/invite", { share_level: shareLevel }); setInvite(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const redeem = async () => {
    if (!code.trim()) return;
    try { const { data } = await api.post("/buddy/redeem", { code }); toast.success(`You're now supporting ${data.owner_name}. 💚`); setCode(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setLevel = async (bid, level) => {
    try { await api.patch(`/buddy/${bid}`, { share_level: level }); toast.success("Sharing updated."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (bid) => {
    try { await api.delete(`/buddy/${bid}`); toast.success("Connection removed."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openView = async (ownerId) => {
    try { const { data } = await api.get(`/buddy/view/${ownerId}`); setView(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-8">
      <SecureAccountCard />
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Buddy & Accountability</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">Invite someone you trust to walk alongside you. You choose exactly what they can see — and can change it any time.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-head text-lg font-semibold text-[#2C363F]"><Users className="h-5 w-5 text-[#4A7c59]" /> Invite a buddy</h3>
          <p className="mt-1 text-sm text-muted-foreground">Generate a code and share it with your buddy. They redeem it in their own account.</p>
          <div className="mt-4 space-y-3">
            <div>
              <Label>They can see</Label>
              <Select value={shareLevel} onValueChange={setShareLevel}>
                <SelectTrigger data-testid="share-level" className="mt-1 rounded-xl py-6"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="milestones">Milestones only</SelectItem><SelectItem value="full">Milestones + daily logs</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="gen-invite" onClick={genInvite} className="w-full rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]">Generate invite code</Button>
            {invite && (
              <div className="flex items-center justify-between rounded-xl bg-[#4A7c59]/10 p-4">
                <span className="font-head text-2xl font-bold tracking-widest text-[#4A7c59]" data-testid="invite-code">{invite.code}</span>
                <button onClick={() => { navigator.clipboard?.writeText(invite.code); toast.success("Code copied."); }} className="text-[#4A7c59]"><Copy className="h-5 w-5" /></button>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-head text-lg font-semibold text-[#2C363F]"><Heart className="h-5 w-5 text-[#E8907E]" /> Support someone</h3>
          <p className="mt-1 text-sm text-muted-foreground">Got a code from someone tapering? Enter it to become their buddy.</p>
          <div className="mt-4 flex gap-3">
            <Input data-testid="redeem-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" className="rounded-xl py-6 uppercase" />
            <Button data-testid="redeem-btn" onClick={redeem} className="rounded-full bg-[#E8907E] py-6 hover:bg-[#d97a66]">Connect</Button>
          </div>
        </div>
      </div>

      {data.supporters.length > 0 && (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-head text-lg font-semibold text-[#2C363F]"><Send className="h-5 w-5 text-[#4A7c59]" /> Send a check-in</h3>
          <p className="mt-1 text-sm text-muted-foreground">Let your support team know how you're doing today.</p>
          <div className="mt-4 flex gap-2">
            {MOOD_EMOJI.map((m, i) => (
              <button key={i} onClick={() => setCi({ ...ci, mood: i + 1 })} data-testid={`ci-mood-${i + 1}`} className={`flex h-11 w-11 items-center justify-center rounded-full text-xl transition-transform duration-200 ${ci.mood === i + 1 ? "scale-110 bg-[#4A7c59]/15 ring-2 ring-[#4A7c59]" : "bg-muted"}`}>{m}</button>
            ))}
          </div>
          <Textarea data-testid="ci-note" value={ci.note} onChange={(e) => setCi({ ...ci, note: e.target.value })} placeholder="Anything you'd like to share (optional)" className="mt-3 rounded-xl" />
          <Button data-testid="send-checkin" onClick={sendCheckin} className="mt-3 rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Share check-in</Button>
        </div>
      )}

      {data.supporters.length > 0 && (
        <div>
          <h2 className="font-head text-2xl font-bold text-[#2C363F]">Your support team</h2>
          <p className="text-sm text-muted-foreground">People who can see your progress.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {data.supporters.map((b) => (
              <div key={b.id} className="rounded-2xl border border-border bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="font-head font-semibold text-[#2C363F]">{b.buddy_name}</span>
                  <button onClick={() => remove(b.id)} data-testid={`remove-${b.id}`} className="text-muted-foreground hover:text-[#8a4436]"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="mt-3"><Label className="text-xs">Sees</Label>
                  <Select value={b.share_level} onValueChange={(v) => setLevel(b.id, v)}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="milestones">Milestones only</SelectItem><SelectItem value="full">Milestones + daily logs</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.supporting.length > 0 && (
        <div>
          <h2 className="font-head text-2xl font-bold text-[#2C363F]">People you're supporting</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {data.supporting.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-2xl border border-border bg-white p-5 shadow-sm">
                <div><span className="font-head font-semibold text-[#2C363F]">{b.owner_name}</span><div className="text-xs text-muted-foreground">You can see: {LEVELS[b.share_level]}</div></div>
                <Button data-testid={`view-${b.owner_id}`} onClick={() => openView(b.owner_id)} variant="outline" className="rounded-full border-[#4A7c59]/30 text-[#4A7c59]"><Eye className="mr-1 h-4 w-4" /> View</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {checkins.length > 0 && (
        <div>
          <h2 className="font-head text-2xl font-bold text-[#2C363F]">Check-ins from people you support</h2>
          <div className="mt-4 space-y-3" data-testid="checkins-feed">
            {checkins.map((c) => (
              <div key={c.id} className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#8FB8DE]/15 text-2xl">{c.mood ? MOOD_EMOJI[c.mood - 1] : <MessageCircle className="h-5 w-5 text-[#2C4763]" />}</div>
                <div>
                  <p className="font-head font-semibold text-[#2C363F]">{c.owner_name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString("en-GB")}</p>
                  {c.note && <p className="mt-1 text-sm text-[#2C363F]">{c.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head">{view?.owner_name}'s progress</DialogTitle><DialogDescription>What this person has chosen to share with you.</DialogDescription></DialogHeader>
          {view && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[#4A7c59]/10 p-4"><div className="flex items-center gap-1 text-xs text-muted-foreground"><Sparkles className="h-3.5 w-3.5" /> Days tracked</div><div className="font-head text-2xl font-bold text-[#4A7c59]">{view.milestones.days_tracked}</div></div>
                <div className="rounded-2xl bg-[#8FB8DE]/15 p-4"><div className="flex items-center gap-1 text-xs text-muted-foreground"><TrendingDown className="h-3.5 w-3.5" /> Dose reduced</div><div className="font-head text-2xl font-bold text-[#2C4763]">{view.milestones.total_reduction || 0} mg</div></div>
              </div>
              {view.share_level === "full" && view.logs ? (
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {view.logs.slice().reverse().map((l) => (
                    <div key={l.id} className="rounded-xl border border-border p-3 text-sm"><span className="font-medium">{l.date}</span> · dose {l.dose_mg ?? "—"}mg · mood {l.mood}/5{l.notes ? ` · ${l.notes}` : ""}</div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">They've chosen to share milestones only. That's their call, and it's respected. 💚</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
